import {
  Injectable,
  Inject,
  Optional
} from '@angular/core';
import {
  Actions,
  Effect,
  ofType
} from '@ngrx/effects';
import {
  LoadTableData,
  TableActionTypes,
  ArchiveRow,
  RestoreRow,
  ViewRow,
  SetExpandedRow,
  EditRow,
  SetExpandedRowFullMode,
  ClearExpandedRowId,
  DeleteRow
} from './table.actions';
import {
  mergeMap,
  map,
  filter,
  switchMap,
  first
} from 'rxjs/operators';
import { DigiAIXCoreAppState } from '../app-state';
import {
  Store,
  select
} from '@ngrx/store';
import {
  selectTableState,
  isExpandViewMode,
  isDialogViewMode,
  isExpandEditMode,
  selectTablesState,
  getTableDeleteMessage,
  isManuelArchive,
  isManuelDelete,
  getTableViewComponentId
} from './table.selectors';
import {
  SubmitSuccessful,
  FormsActionTypes
} from '../forms/forms.actions';
import { OpenDialogWithViewContainer } from '../dialog/dialog.actions';
import { NGXLogger } from 'ngx-logger';
import {
  LoadDocuments,
  RequestDeleteDocument,
  ArchiveDocument,
  RestoreDocument
} from '../documents/document.actions';
import { getRowId } from './utilities';
import {
  DEFAULT_TABLE_QUERY,
  DefaultTableQueryFunction,
  PREFER_WINDOW
} from '../tokens';
import { OpenComponentInWindow } from '../component/component.actions';

@Injectable()
export class TableEffects<AppState extends DigiAIXCoreAppState> {
  @Effect({ resubscribeOnError: true })
  public loadTableData$ = this.actions$.pipe(
    ofType<LoadTableData>(TableActionTypes.LoadTableData),
    mergeMap(action =>
      this.store$.pipe(
        select(selectTableState(action.tableId)),
        first(),
        filter(tableState => !!tableState),
        map(
          tableState =>
            new LoadDocuments(
              action.collectionPath,
              this.defaultTableQuery &&
                this.defaultTableQuery(tableState, tableState.query)
            )
        )
      )
    )
  );

  // TODO : enable SetTableLimit feature
  // @Effect({ resubscribeOnError: true })
  // public setTableLimit$ = this.actions$.pipe(
  //   ofType<SetTableLimit>(TableActionTypes.SetTableLimit),
  //   map((action) => new LoadTableData(action.tableId)),
  // );

  @Effect({ resubscribeOnError: true })
  public archiveRow$ = this.actions$.pipe(
    ofType<ArchiveRow>(TableActionTypes.ArchiveRow),
    filter(action => !!action.collectionPath),
    switchMap(action => this.store$.pipe(
      select(isManuelArchive(action.tableId)),
      first(),
      filter(m => !m),
      map(() => new ArchiveDocument(action.collectionPath, action.rowId))
    )),
  );

  @Effect({ resubscribeOnError: true })
  public restoreRow$ = this.actions$.pipe(
    ofType<RestoreRow>(TableActionTypes.RestoreRow),
    filter(action => !!action.collectionPath),
    switchMap(action => this.store$.pipe(
      select(isManuelArchive(action.tableId)),
      first(),
      filter(m => !m),
      map(() => new RestoreDocument(action.collectionPath, action.rowId))
    )),
  );

  @Effect({ resubscribeOnError: true })
  public submitSuccessfulOfExpaned$ = this.actions$.pipe(
    ofType<SubmitSuccessful<any>>(FormsActionTypes.SubmitSuccessful),
    switchMap(action =>
      this.store$.pipe(
        select(selectTablesState),
        first(),
        map(tableState =>
          Object.entries(tableState).filter(
            ([formId, ts]) => ts.formId === action.formId && ts.expandable.row
          )
        ),
        filter(tables => tables.length !== 0),
        map(tables => tables[0]),
        map(([tableId, state]) => new ClearExpandedRowId(tableId))
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public viewRowInExpanded = this.actions$.pipe(
    ofType<ViewRow>(TableActionTypes.ViewRow),
    mergeMap(action =>
      this.store$.pipe(
        select(isExpandViewMode(action.tableId)),
        first(),
        filter(expandViewMode => expandViewMode),
        map(
          () =>
            new SetExpandedRow(
              action.tableId,
              action.row,
              action.collectionPath,
              false,
              true
            )
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public deleteRow$ = this.actions$.pipe(
    ofType<DeleteRow>(TableActionTypes.DeleteRow),
    filter(action => !!action.collectionPath),
    switchMap(action => this.store$.pipe(
      select(isManuelDelete(action.tableId)),
      first(),
      filter(m => !m),
      switchMap(() =>
        this.store$.pipe(
          select(getTableDeleteMessage(action.tableId)),
          first(),
          map(
            deleteMessage =>
              new RequestDeleteDocument(
                action.collectionPath,
                getRowId(action.row),
                action.tableId,
                [action.tableId],
                deleteMessage
              )
          )
        )
      )
    )),
  );

  @Effect({ resubscribeOnError: true })
  public viewRowInDialog = this.actions$.pipe(
    ofType<ViewRow>(TableActionTypes.ViewRow),
    filter(() => !this.preferWindow),
    mergeMap(action =>
      this.store$.pipe(
        select(isDialogViewMode(action.tableId)),
        first(),
        filter(dialogViewMode => dialogViewMode),
        map(
          () =>
            new OpenDialogWithViewContainer({
              config: {
                data: {
                  tableId: action.tableId,
                  row: action.row,
                  collectionPath: action.collectionPath
                }
              }
            })
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public viewRowInWindow$ = this.actions$.pipe(
    ofType<ViewRow>(TableActionTypes.ViewRow),
    mergeMap(action =>
      this.store$.pipe(
        select(getTableViewComponentId(action.tableId)),
        first(),
        filter(componentId => !!componentId),
        map(
          componentId =>
            new OpenComponentInWindow(componentId, action.row, 'TableEffects::viewRowInWindow$')
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public editRowInExpanded = this.actions$.pipe(
    ofType<EditRow>(TableActionTypes.EditRow),
    mergeMap(action =>
      this.store$.pipe(
        select(isExpandEditMode(action.tableId)),
        first(),
        filter(expandEditMode => expandEditMode),
        switchMap(() => [
          new SetExpandedRow(
            action.tableId,
            action.row,
            action.collectionPath,
            true
          ),
          new SetExpandedRowFullMode(action.tableId, true)
        ])
      )
    )
  );

  public readonly defaultTableQuery:
    | DefaultTableQueryFunction
    | undefined = undefined;

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private logger: NGXLogger,
    @Inject(DEFAULT_TABLE_QUERY) defaultTableQuery: any,
    @Optional() @Inject(PREFER_WINDOW) public preferWindow: boolean,
  ) {
    this.defaultTableQuery = defaultTableQuery || undefined;
  }
}
